// src/services/reminder_mock.js
//
// ############################################################################
// # TEMPORARY. DELETE THIS FILE when the backend reminder endpoints ship.    #
// ############################################################################
//
// Stands in for the reminder-schedule endpoints while the admin workflow is
// reviewed. See Part F of docs/WEEK_INTERVAL_AND_WEEK_START.md in the API repo.
//
//   GET  /message/schedule            both plans
//   PUT  /message/schedule/{interval} save one plan
//
// The shapes below ARE the contract the backend implements, so keep them in
// step with message_schedule. State is in-memory and resets on refresh.
//
// ---------------------------------------------------------------------------
// Why this table is keyed by interval_weeks and not by group
//
// Reminder timing is a property of the cadence shape, not of a cohort: every
// 2-week group wants its "you have not started yet" nudge at the same point in
// the period. Keying on the interval means a group that switches cadence moves
// onto the matching plan automatically, with no second setting to remember.
//
// Why this table is mutable, unlike group_cadence
//
// group_cadence must be append-only because editing it retroactively changes
// which block a completed session belonged to. Reminder timing has no such
// property: changing tomorrow's send time rewrites nothing about the past, and
// what was actually sent is already recorded in message_records. So this is a
// plain UPDATE with updated_by/updated_at, not an effective-dated history.

// Seeded from instance/config/message.toml so cutover is behaviour-neutral.
//
//   new-session-available-sms       0 9  * * 1  Mon 09:00 -> day 0, 09:00
//   new-session-available-email    10 9  * * 1  Mon 09:10 -> day 0, 09:30 *
//   session-not-started-*           0 12 * * 4  Thu 12:00 -> day 3, 12:00
//   session-not-finished-sms        0 13 * * *  daily 13:00
//   session-not-finished-email     10 12 * * *  daily 12:10 -> 12:30 *
//   implementation-reminder-sms     0 11 * * *  daily 11:00
//   implementation-reminder-email  10 11 * * *  daily 11:10 -> 11:30 *
//
// * The dispatcher wakes every 30 minutes, so send times land on :00 or :30.
//   Three email sends move by at most 20 minutes. Note that session_not_finished
//   sends email BEFORE sms today (12:10 vs 13:00) -- per-channel times preserve
//   that; a single time per message with an automatic email offset would have
//   silently reversed it.
const SEED = {
  1: [
    { message_title: "new_session_available", channel: "sms", trigger_type: "block_relative", day_offset: 0, send_time: "09:00", enabled: true },
    { message_title: "new_session_available", channel: "email", trigger_type: "block_relative", day_offset: 0, send_time: "09:30", enabled: true },
    { message_title: "session_not_started", channel: "sms", trigger_type: "block_relative", day_offset: 3, send_time: "12:00", enabled: true },
    { message_title: "session_not_started", channel: "email", trigger_type: "block_relative", day_offset: 3, send_time: "12:00", enabled: true },
    { message_title: "session_not_finished", channel: "sms", trigger_type: "daily", day_offset: null, send_time: "13:00", enabled: true },
    { message_title: "session_not_finished", channel: "email", trigger_type: "daily", day_offset: null, send_time: "12:30", enabled: true },
    { message_title: "implementation_reminder", channel: "sms", trigger_type: "daily", day_offset: null, send_time: "11:00", enabled: true },
    { message_title: "implementation_reminder", channel: "email", trigger_type: "daily", day_offset: null, send_time: "11:30", enabled: true },
  ],
  // Day 3 of a 14-day period is far too early for a "not started yet" nudge,
  // so the 2-week plan defaults it to the midpoint. This is the setting the
  // whole feature exists to make adjustable.
  2: [
    { message_title: "new_session_available", channel: "sms", trigger_type: "block_relative", day_offset: 0, send_time: "09:00", enabled: true },
    { message_title: "new_session_available", channel: "email", trigger_type: "block_relative", day_offset: 0, send_time: "09:30", enabled: true },
    { message_title: "session_not_started", channel: "sms", trigger_type: "block_relative", day_offset: 7, send_time: "12:00", enabled: true },
    { message_title: "session_not_started", channel: "email", trigger_type: "block_relative", day_offset: 7, send_time: "12:00", enabled: true },
    { message_title: "session_not_finished", channel: "sms", trigger_type: "daily", day_offset: null, send_time: "13:00", enabled: true },
    { message_title: "session_not_finished", channel: "email", trigger_type: "daily", day_offset: null, send_time: "12:30", enabled: true },
    { message_title: "implementation_reminder", channel: "sms", trigger_type: "daily", day_offset: null, send_time: "11:00", enabled: true },
    { message_title: "implementation_reminder", channel: "email", trigger_type: "daily", day_offset: null, send_time: "11:30", enabled: true },
  ],
};

let store = null;

function ensure() {
  if (!store) store = JSON.parse(JSON.stringify(SEED));
  return store;
}

export const reminderMock = {
  // GET /message/schedule
  list() {
    const db = ensure();
    return {
      plans: Object.keys(db).map((k) => ({
        interval_weeks: Number(k),
        rows: JSON.parse(JSON.stringify(db[k])),
      })),
      // Read-only. Reported by the backend rather than hardcoded in the page so
      // that if onboarding's cron ever moves, the admin sees the real value
      // instead of a stale copy. Mirrors onboarding-reminder-email in
      // instance/config/message.toml: "0 10 * * 4".
      fixed: [
        {
          message_title: "onboarding_reminder",
          channel: "email",
          schedule_label: "Every Thursday",
          send_time: "10:00",
          enabled: true,
          source: "message.toml",
        },
      ],
      // How often the dispatcher wakes, which is what constrains send times to
      // the hour and half hour. Surfaced so the page can explain the limit
      // rather than asserting it.
      tick_minutes: 30,
      // A 2-week plan is stored and editable but cannot fire until
      // group_cadence exists: which of two Mondays opens a block is
      // undecidable without a stored anchor. The dispatcher skips these.
      inert_intervals: [2],
    };
  },

  // PUT /message/schedule/{interval_weeks}
  save(intervalWeeks, rows) {
    const db = ensure();
    const key = String(intervalWeeks);
    if (!db[key]) throw new Error(`No plan for a ${intervalWeeks}-week cadence.`);

    // The server re-runs this; the form's own checks are advisory.
    const maxOffset = intervalWeeks * 7 - 1;
    for (const r of rows) {
      if (r.day_offset != null) {
        if (!Number.isInteger(r.day_offset) || r.day_offset < 0 || r.day_offset > maxOffset) {
          throw new Error(
            `Day offset for ${r.message_title} must be between 0 and ${maxOffset} ` +
              `for a ${intervalWeeks}-week period.`
          );
        }
      }
      if (!/^([01]\d|2[0-3]):(00|30)$/.test(r.send_time || "")) {
        throw new Error(
          `Send time for ${r.message_title} (${r.channel}) must be on the hour ` +
            "or half hour."
        );
      }
    }

    db[key] = JSON.parse(JSON.stringify(rows));
    return { message: "Reminder schedule saved." };
  },

  reset() {
    store = null;
  },
};

export default reminderMock;
